"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchResults, deleteResult, clearError } from "../store/slices/resultSlice"
import AddResult from "./AddResult"
import EditResult from "./EditResult"
import {
  BookOpen,
  Plus,
  CheckCircle,
  XCircle,
  Percent,
  Search,
  Edit,
  Trash2,
  Loader2,
  X,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"

// --- Aesthetic Configuration ---
const PRIMARY_COLOR = "blue-600"
const PRIMARY_HOVER = "blue-700"
const SUCCESS_COLOR = "green-600"
const DANGER_COLOR = "red-600"
const PURPLE_COLOR = "purple-600"

const ResultsComponent = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { results, loading, error } = useSelector((state) => state.results)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingResult, setEditingResult] = useState(null)
  const [viewingResult, setViewingResult] = useState(null)
  const [message, setMessage] = useState({ text: "", type: "" })
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Debug user and results data
  useEffect(() => {
    console.log("User:", user)
    console.log("All Results:", results)
  }, [user, results])

  // Filter results based on role
  const visibleResults =
    user?.role === "student"
      ? results.filter((r) => {
          const match = r.student_id === user?.id
          if (!match) {
            console.log(`Result ${r.result_id} skipped: student_id=${r.student_id}, user.id=${user?.id}`)
          }
          return match
        })
      : results

  // Filter results based on search term
  const filteredResults = visibleResults.filter(
    (result) =>
      result.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.student_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.exam_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.result_id?.toString().includes(searchTerm),
  )

  // Pagination logic
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedResults = filteredResults.slice(startIndex, endIndex)

  useEffect(() => {
    dispatch(fetchResults())
  }, [dispatch])

  useEffect(() => {
    if (error) {
      setMessage({ text: `Error: ${error}`, type: "error" })
      dispatch(clearError())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3000)
      return () => clearTimeout(timer)
    }
  }, [message])

  const handleEdit = (result) => {
    setEditingResult(result)
    setShowEditModal(true)
  }

  const handleDelete = (resultId) => {
    if (window.confirm("Are you sure you want to delete this result? This action cannot be undone.")) {
      dispatch(deleteResult(resultId))
        .unwrap()
        .then(() => {
          setMessage({ text: "Result deleted successfully!", type: "success" })
          dispatch(fetchResults())
        })
        .catch((err) => {
          setMessage({
            text: `Error deleting result: ${err.message || err}`,
            type: "error",
          })
        })
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value))
    setCurrentPage(1) // Reset to first page when items per page changes
  }

  // Stats calculations
  const totalResults = filteredResults.length
  const passCount = filteredResults.filter((r) => r.status === "Pass").length
  const failCount = filteredResults.filter((r) => r.status === "Fail").length
  const averageMarks =
    totalResults > 0
      ? Math.round(filteredResults.reduce((sum, r) => sum + (Number.parseInt(r.marks) || 0), 0) / totalResults)
      : 0

  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 text-${PRIMARY_COLOR}`}>
        <Loader2 className="animate-spin h-10 w-10 text-blue-500" />
        <span className="ml-2 text-gray-600 mt-2 text-lg">Loading results...</span>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-0">
      {/* Header and Add Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <BookOpen className={`w-8 h-8 text-${PRIMARY_COLOR}`} />
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {user?.role === "student" ? "My Exam Results" : "Results Management"}
          </h2>
        </div>

        {(user?.role === "admin" || user?.role === "super_admin") && (
          <button
            onClick={() => setShowAddModal(true)}
            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white font-semibold py-2 px-5 rounded-xl shadow-md transition-all transform hover:scale-[1.02]`}
          >
            <Plus className="w-5 h-5" />
            Add New Result
          </button>
        )}
      </div>

      {/* Message Alerts */}
      {message.text && (
        <div
          className={`p-4 rounded-xl shadow-md border-l-4 ${
            message.type === "success"
              ? `border-green-500 bg-green-50 text-green-800`
              : `border-red-500 bg-red-50 text-red-800`
          } font-medium`}
        >
          <p>{message.text}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-blue-500 transition-shadow hover:shadow-2xl">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase">Total Results</h3>
              <p className="text-3xl font-bold text-gray-900">{totalResults}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-green-500 transition-shadow hover:shadow-2xl">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase">Pass Count</h3>
              <p className="text-3xl font-bold text-green-600">{passCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-red-500 transition-shadow hover:shadow-2xl">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-red-100 rounded-full">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase">Fail Count</h3>
              <p className="text-3xl font-bold text-red-600">{failCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-xl border-l-4 border-purple-500 transition-shadow hover:shadow-2xl">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <Percent className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase">Avg. Marks</h3>
              <p className="text-3xl font-bold text-purple-600">{averageMarks}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "student") && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student name, code, exam name, or result ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            />
          </div>
        </div>
      )}

      {/* Results Table */}
      <div className="space-y-4">
        {paginatedResults.length === 0 ? (
          <div className="bg-white p-12 rounded-xl shadow-lg text-center border-2 border-dashed border-gray-300">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-600">No Results Found</p>
            <p className="text-gray-500 mt-1">
              {user?.role === "student"
                ? "Your results will appear here once declared by the administrator."
                : "Add new results using the button above or adjust your search criteria."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Result ID
                    </th>
                    {(user?.role === "admin" || user?.role === "super_admin") && (
                      <>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[150px]">
                          Student Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                          Student Code
                        </th>
                      </>
                    )}
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[150px]">
                      Exam Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[80px]">
                      Marks
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[120px]">
                      Declared At
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedResults.map((result) => (
                    <tr key={result.result_id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {result.result_id}
                      </td>
                      {(user?.role === "admin" || user?.role === "super_admin") && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {result.student_name || "Unknown Student"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {result.student_code || "N/A"}
                          </td>
                        </>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{result.exam_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {result.marks || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
                            result.status === "Pass"
                              ? "bg-green-100 text-green-800"
                              : result.status === "Fail"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {result.status || "Not Set"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {result.declared_at ? new Date(result.declared_at).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setViewingResult(result)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors group"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          </button>
                          {(user?.role === "admin" || user?.role === "super_admin") && (
                            <>
                              <button
                                onClick={() => handleEdit(result)}
                                className={`p-2 text-${PRIMARY_COLOR} hover:bg-blue-100 rounded-full transition-colors group`}
                                title="Edit Result"
                              >
                                <Edit className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                              <button
                                onClick={() => handleDelete(result.result_id)}
                                className={`p-2 text-${DANGER_COLOR} hover:bg-red-100 rounded-full transition-colors group`}
                                title="Delete Result"
                              >
                                <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === page
                          ? `bg-${PRIMARY_COLOR} text-white`
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal Overlay */}
      {showAddModal && (user?.role === "admin" || user?.role === "super_admin") && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Record New Result</h3>
            <AddResult setShowAddModal={setShowAddModal} />
          </div>
        </div>
      )}

      {/* Edit Modal Overlay */}
      {showEditModal && (user?.role === "admin" || user?.role === "super_admin") && editingResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
              Edit Result (ID: {editingResult.result_id})
            </h3>
            <EditResult result={editingResult} setShowEditModal={setShowEditModal} />
          </div>
        </div>
      )}

      {/* View Result Details Modal */}
      {viewingResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="text-2xl font-bold text-gray-800">Result Details</h3>
              <button
                onClick={() => setViewingResult(null)}
                className="p-2 text-gray-500 hover:text-gray-900 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Result ID:</strong>{" "}
                <span className="font-semibold text-gray-900">{viewingResult.result_id}</span>
              </p>
              <p>
                <strong>Exam Name:</strong> {viewingResult.exam_name}
              </p>
              <p>
                <strong>Student Name:</strong> {viewingResult.student_name || "N/A"}
              </p>
              <p>
                <strong>Student Code:</strong> {viewingResult.student_code || "N/A"}
              </p>
              <p>
                <strong>Marks Obtained:</strong>{" "}
                <span className="font-bold text-lg text-blue-700">{viewingResult.marks || "—"}</span>
              </p>
              <p>
                <strong>Status:</strong>
                <span
                  className={`font-bold ml-2 ${viewingResult.status === "Pass" ? "text-green-600" : "text-red-600"}`}
                >
                  {viewingResult.status || "Not Set"}
                </span>
              </p>
              <p>
                <strong>Declared Date:</strong>{" "}
                {viewingResult.declared_at ? new Date(viewingResult.declared_at).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ResultsComponent
